import React from 'react';
import { IoTime, IoCalendarOutline, IoDocument, IoPencil } from 'react-icons/io5';
import { Deadline, DeadlineType, FilterType } from '../index';

interface ListViewProps {
  deadlines: Deadline[];
  groupedDeadlines: Record<string, Deadline[]>;
  timeFilter: FilterType;
  setTimeFilter: (filter: FilterType) => void;
  sortBy: string;
  setSortBy: (sort: 'date_earliest' | 'date_latest' | 'priority' | 'school_name') => void;
  onEditDeadline: (deadline: Deadline) => void;
  getTypeIcon: (type: DeadlineType) => JSX.Element;
  getTypeColor: (type: DeadlineType) => string;
}

const ListView: React.FC<ListViewProps> = ({
  deadlines,
  groupedDeadlines,
  timeFilter,
  setTimeFilter,
  sortBy,
  setSortBy,
  onEditDeadline,
  getTypeIcon,
  getTypeColor,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleString('default', { month: 'short' }).toUpperCase(),
      fullDate: date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
  };

  const getUrgencyClass = (daysLeft: number) => {
    if (daysLeft < 0) return 'overdue';
    if (daysLeft <= 7) return 'urgent';
    if (daysLeft <= 14) return 'warning';
    return 'normal';
  };

  const getStatusLabel = (status: Deadline['status'], progress: number) => {
    switch (status) {
      case 'completed':
        return 'Complete';
      case 'in_progress':
        return `In Progress (${progress}%)`;
      case 'not_started':
        return 'Not Started';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="list-view">
      {/* Filter Controls */}
      <div className="list-filters">
        <div className="time-filters">
          {[
            { key: 'all', label: 'All Deadlines' },
            { key: 'this_week', label: 'This Week' },
            { key: 'this_month', label: 'This Month' },
            { key: 'overdue', label: 'Overdue' },
          ].map(filter => (
            <button
              key={filter.key}
              className={`filter-btn ${timeFilter === filter.key ? 'active' : ''}`}
              onClick={() => setTimeFilter(filter.key as FilterType)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        
        <select 
          className="sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
        >
          <option value="date_earliest">Sort by Date (Earliest)</option>
          <option value="date_latest">Sort by Date (Latest)</option>
          <option value="priority">Sort by Priority</option>
          <option value="school_name">Sort by School Name</option>
        </select>
      </div>

      {/* Deadlines List */}
      <div className="deadlines-list">
        {Object.keys(groupedDeadlines).length === 0 ? (
          <div className="empty-state">
            <p>No deadlines found matching your current filters.</p>
          </div>
        ) : (
          Object.entries(groupedDeadlines).map(([dateString, dayDeadlines]) => {
            const { day, month } = formatDate(dayDeadlines[0].dueDate);
            
            return (
              <div key={dateString} className="deadline-group">
                <div className="date-header">
                  <div className="date-box">
                    <span className="date-day">{day}</span>
                    <span className="date-month">{month}</span>
                  </div>
                  <div className="date-line"></div>
                  <span className="deadline-count">
                    {dayDeadlines.length} deadline{dayDeadlines.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="deadline-items">
                  {dayDeadlines.map(deadline => (
                    <div 
                      key={deadline.id} 
                      className={`deadline-card ${getUrgencyClass(deadline.daysLeft)}`}
                      data-type={deadline.type}
                    >
                      <div className="deadline-content">
                        <div className="deadline-main">
                          <div className={`deadline-icon deadline-icon-${getTypeColor(deadline.type)}`}>
                            {getTypeIcon(deadline.type)}
                          </div>
                          
                          <div className="deadline-info">
                            <div className="deadline-header-line">
                              <h3 className="deadline-name">{deadline.name}</h3>
                              <div className="deadline-badges">
                                <span className={`type-badge type-badge-${getTypeColor(deadline.type)}`}>
                                  {deadline.type === 'application' && 'Application Deadline'}
                                  {deadline.type === 'scholarship' && 'Scholarship Deadline'}
                                  {deadline.type === 'opportunity' && 'Important Opportunity'}
                                </span>
                                <span className={`urgency-badge urgency-${getUrgencyClass(deadline.daysLeft)}`}>
                                  {deadline.daysLeft < 0 ? `${Math.abs(deadline.daysLeft)} days overdue` : 
                                   deadline.daysLeft === 0 ? 'Due today' :
                                   `${deadline.daysLeft} days left`}
                                </span>
                              </div>
                            </div>
                            
                            <p className="deadline-description">{deadline.description}</p>
                            
                            <div className="deadline-meta">
                              <div className="meta-item">
                                <IoCalendarOutline />
                                <span>Due: {new Date(deadline.dueDate).toLocaleDateString('en-US', { 
                                  month: 'long', day: 'numeric', year: 'numeric' 
                                })}</span>
                              </div>
                              <div className="meta-item">
                                <IoTime />
                                <span>{deadline.dueTime} {deadline.dueDate.includes('2025-01') ? 'EST' : 'PST'}</span>
                              </div>
                              <div className="meta-item">
                                <IoDocument />
                                <span>
                                  Status: {getStatusLabel(deadline.status, deadline.progress)}
                                  {deadline.estimatedTimeHours > 0 && (
                                    <span className="time-remaining"> • Est. time remaining: {deadline.estimatedTimeHours} hrs</span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="deadline-actions">
                          <button 
                            className="edit-btn"
                            onClick={() => onEditDeadline(deadline)}
                            title="Edit deadline"
                          >
                            <IoPencil />
                          </button>
                          <button className="view-details-btn">
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Load More */}
      {deadlines.length > 10 && (
        <div className="load-more-section">
          <button className="load-more-btn">
            <IoTime /> Load More Deadlines
          </button>
        </div>
      )}
    </div>
  );
};

export default ListView;